// ============================================================================
// notificationModel.ts — ЕДИНАЯ модель карточки уведомления (SSoT).
//
// Одна и та же карточка используется:
//   • в окне уведомлений desktop (отдельное окно WebView2, /notify);
//   • во внутриприложенческом попапе (чат и уведомления платформы);
//   • данные для Web Push (те же поля title/body/context/convId).
//
// Никаких UI-зависимостей: модель пригодна и для React-компонента, и для
// лёгкой страницы окна уведомлений.
// ============================================================================

export type NotificationKind = 'chat' | 'platform';

/** Тема оформления: CSS-переменные приложения (передаётся в отдельное окно). */
export interface NotificationTheme {
  bg: string;
  card: string;
  text: string;
  muted: string;
  primary: string;
  primaryFg: string;
  border: string;
}

/** Карточка уведомления (единый контракт клиента и окна desktop). */
export interface AppNotification {
  /** Уникальный id (дедупликация стека). */
  id: string;
  /** Чат или системное уведомление платформы. */
  kind: NotificationKind;
  /** Имя отправителя / заголовок события. */
  title: string;
  /** Название чата / источник события (строка контекста под заголовком). */
  context?: string;
  /** Текст сообщения (многострочный; обрезается на карточке). */
  body: string;
  /** Время события, мс (Unix epoch). */
  ts: number;
  /** Чат для действия «Открыть» (чат). */
  convId?: string;
  /** Внутренний маршрут для действия «Открыть» (платформенные уведомления). */
  url?: string;
  /** data URL аватара отправителя (если уже загружен). */
  avatarUrl?: string;
  /** Тема (только для отдельного окна desktop). */
  theme?: NotificationTheme;
}

/** Событие «серверные уведомления отмечены прочитанными» (колокольчик). */
export const NOTIFICATIONS_READ_EVENT = 'hydra:notifications-read';

/** Сколько карточек одновременно держит стек. */
export const NOTIFICATION_STACK_LIMIT = 3;
/** Автоскрытие карточки в попапе/окне, мс. */
export const NOTIFICATION_CARD_TTL_MS = 8000;

/** Инициалы для круга-аватара (до 2 символов). */
export function notificationInitials(name: string): string {
  const s = String(name || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/);
  if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

/** Время карточки: сегодня — ЧЧ:ММ, иначе ДД.ММ ЧЧ:ММ. */
export function formatNotificationTime(ts: number): string {
  const value = Number.isFinite(ts) && ts > 0 ? ts : Date.now();
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) return `${hh}:${mm}`;
  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mo} ${hh}:${mm}`;
}

/** Текст карточки: пустое сообщение (вложение без подписи) — понятная метка. */
export function notificationBodyText(body: string): string {
  const text = String(body || '').trim();
  return text || '[вложение]';
}
